import { useEffect, useState } from "react";

// US-082 (ADR-023) — user avatar with an initials fallback. Renders the image
// from GET /api/users/:id/avatar (cookie auth rides the same-origin <img> GET);
// on a 404/error it swaps in the initials. `version` busts the cache after an
// upload so the new image shows immediately.

function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((p) => p[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

interface UserAvatarProps {
  userId: string;
  name: string;
  /** px size (square). */
  size?: number;
  /** Bump to force a reload after an upload. */
  version?: number;
  className?: string;
}

export function UserAvatar({ userId, name, size = 36, version, className }: UserAvatarProps) {
  const [failed, setFailed] = useState(false);
  // Reset the failed state if the target user or version changes.
  useEffect(() => {
    setFailed(false);
  }, [userId, version]);

  const dimension = { width: size, height: size };
  const base = `flex items-center justify-center rounded-full overflow-hidden shrink-0 ${className ?? ""}`;

  if (failed) {
    return (
      <div
        style={dimension}
        className={`${base} bg-primary/10 text-primary text-xs font-semibold`}
        aria-label={name}
      >
        {initialsOf(name)}
      </div>
    );
  }

  const src = version ? `/api/users/${userId}/avatar?v=${version}` : `/api/users/${userId}/avatar`;
  return (
    <img
      src={src}
      alt={name}
      style={dimension}
      onError={() => setFailed(true)}
      className={`${base} object-cover bg-muted`}
    />
  );
}
